import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SettingsTab } from '../../components/settings/SettingsTab'
import { AnalyticsTab } from '../../components/settings/AnalyticsTab'

const API_URL = import.meta.env.VITE_API_URL || 'https://qihmh0pa24.execute-api.us-west-2.amazonaws.com'

type TabType = 'analytics' | 'settings'

export function CustomerDashboardPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('analytics')
  const [clientId, setClientId] = useState<string | null>(null)
  const [agentName, setAgentName] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const jwt = localStorage.getItem('hellabots_jwt')
    const storedClientId = localStorage.getItem('hellabots_clientId')

    if (!jwt || !storedClientId) {
      navigate('/settings')
      return
    }

    setClientId(storedClientId)
    fetchClientInfo(jwt)
  }, [navigate])

  const fetchClientInfo = async (jwt: string) => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('hellabots_jwt')
          localStorage.removeItem('hellabots_clientId')
          navigate('/settings')
          return
        }
        throw new Error(data.error || 'Failed to fetch settings')
      }

      setAgentName(data.settings.agentName || data.settings.name || 'Agent')
    } catch (err: any) {
      console.error('Error fetching client info:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('hellabots_jwt')
    localStorage.removeItem('hellabots_clientId')
    navigate('/settings')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-500 px-6 py-8 text-white">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">Welcome, {agentName}</h1>
                <p className="text-purple-100">Manage your HellaBots dashboard</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'analytics'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Analytics
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Settings
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'analytics' && clientId && <AnalyticsTab clientId={clientId} />}
            {activeTab === 'settings' && <SettingsTab onLogout={handleLogout} />}
          </div>
        </div>

        {/* Client ID Info */}
        {clientId && (
          <div className="mt-4 text-center text-sm text-gray-500">
            Client ID: <span className="font-mono">{clientId}</span>
          </div>
        )}
      </div>
    </div>
  )
}
